import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { Button } from "../ui/button";

const Header = ({ onDiagnosticClick, onDemoClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Product", href: "#what-tenacity-does" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "Modules", href: "#modules" },
    { label: "Enterprise Context", href: "#pillars" },
    { label: "Security", href: "#security" },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "glass border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <div className="px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a 
              href="/" 
              className="flex items-center gap-2"
              data-testid="header-logo"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#06B6D4] flex items-center justify-center">
                <span className="text-[#050505] font-bold text-lg font-['Outfit']">T</span>
              </div>
              <span className="text-xl font-bold tracking-tight text-white font-['Outfit']">
                OnTenacity
              </span>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => scrollToSection(link.href)}
                  className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-200"
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </button>
              ))}
            </nav>

            {/* Desktop CTAs */}
            <div className="hidden lg:flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={onDemoClick}
                className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                data-testid="header-demo-btn"
              >
                Request Demo
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-white"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 menu-overlay pt-20"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8">
              {/* Mobile Nav Description */}
              <p className="text-[#A1A1AA] text-sm mb-8 max-w-xs">
                Enterprise intelligence and memory infrastructure for leadership teams.
              </p>

              {/* Mobile Nav Links */}
              <nav className="flex flex-col gap-4 mb-8">
                {navLinks.map((link, index) => (
                  <motion.button
                    key={link.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => scrollToSection(link.href)}
                    className="text-left text-2xl font-semibold text-white hover:text-[#00E5FF] transition-colors font-['Outfit']"
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </motion.button>
                ))}
              </nav>

              {/* Mobile CTAs */}
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onDiagnosticClick();
                  }}
                  className="btn-primary w-full py-4 rounded-full text-base"
                  data-testid="mobile-diagnostic-btn"
                >
                  Start AI Diagnostic
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onDemoClick();
                  }}
                  className="btn-secondary w-full py-4 rounded-full text-base"
                  data-testid="mobile-demo-btn"
                >
                  Request Demo
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
